import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Loader2, Save } from "lucide-react";

const ROLE_LABELS = {
  developer: "Developer",
  national_admin: "National Admin",
  regional_admin: "Regional Admin",
  chapter_leader: "Chapter Leader",
  scout_leader: "Scout Leader",
  cubs_leader: "Cubs Leader",
  senior_scout_leader: "Senior Scout Leader",
  scout: "Scout",
  parent: "Parent",
  consultant: "Consultant",
  guest: "Guest",
};

export default function RolePermissionsManager() {
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [matrix, setMatrix] = useState({});
  const [editorRole, setEditorRole] = useState("");
  const [devOnlyRows, setDevOnlyRows] = useState(["developer", "national_admin"]);
  const [dirty, setDirty] = useState(new Set());
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/role-permissions")
      .then(r => {
        setRoles(r.data?.roles || []);
        setPermissions(r.data?.permissions || []);
        setMatrix(r.data?.matrix || {});
        setEditorRole(r.data?.editor_role || "");
        setDevOnlyRows(r.data?.dev_only_rows || ["developer", "national_admin"]);
        setDirty(new Set());
      })
      .catch(() => toast.error("Failed to load role permissions"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const isLocked = (role) => {
    // 'developer' may edit any row (incl. its own + national_admin).
    // 'national_admin' may edit everything except dev-only rows.
    if (editorRole === "developer") return false;
    if (devOnlyRows.includes(role)) return true;
    return false;
  };

  const toggle = (role, key) => {
    setMatrix(prev => ({
      ...prev,
      [role]: { ...prev[role], [key]: !prev[role][key] },
    }));
    setDirty(prev => new Set(prev).add(role));
  };

  const saveAll = async () => {
    if (dirty.size === 0) { toast("Nothing to save"); return; }
    setSaving(true);
    try {
      await Promise.all([...dirty].map(role => api.put(`/role-permissions/${role}`, { permissions: matrix[role] })));
      toast.success(`Saved ${dirty.size} role${dirty.size === 1 ? "" : "s"}`);
      setDirty(new Set());
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card className="clay-card p-10 flex items-center justify-center gap-2 text-muted-foreground mt-4">
        <Loader2 size={16} className="animate-spin"/> Loading permissions matrix…
      </Card>
    );
  }

  return (
    <div className="mt-4 space-y-4" data-testid="role-permissions-manager">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-2xl bg-[hsl(149,40%,30%)]/15 text-[hsl(149,40%,30%)] flex items-center justify-center flex-shrink-0"><ShieldCheck size={18}/></div>
          <div>
            <h3 className="font-display font-bold text-xl">Roles & permissions</h3>
            <p className="text-xs text-muted-foreground max-w-xl">
              Toggle what each role can do. {permissions.length} curated permissions × {roles.length} roles.
              Changes apply to new actions immediately — existing hard-coded role checks still work as a safety net.
            </p>
          </div>
        </div>
        <Button
          className="btn-pill bg-[hsl(149,40%,30%)]"
          disabled={saving || dirty.size === 0}
          onClick={saveAll}
          data-testid="role-perms-save"
        >
          <Save size={14} className="mr-2"/>
          {saving ? "Saving…" : dirty.size > 0 ? `Save ${dirty.size} role${dirty.size === 1 ? "" : "s"}` : "No changes"}
        </Button>
      </div>

      <Card className="clay-card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/60 border-b border-border">
                <th className="text-left px-4 py-3 font-semibold sticky left-0 bg-muted/60 z-10 min-w-[220px]">Permission</th>
                {roles.map(r => (
                  <th key={r} className="text-center px-3 py-3 font-semibold min-w-[120px]">
                    <div className="text-xs">{ROLE_LABELS[r] || r}</div>
                    {dirty.has(r) && <Badge className="rounded-full mt-1 bg-[hsl(32,87%,55%)] text-[hsl(155,60%,8%)] text-[9px] uppercase">edited</Badge>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {permissions.map((p, idx) => (
                <tr key={p.key} className={idx % 2 === 0 ? "bg-background" : "bg-muted/20"}>
                  <td className="px-4 py-2.5 sticky left-0 z-10" style={{ background: idx % 2 === 0 ? "hsl(var(--background))" : "hsl(var(--muted) / 0.2)" }}>
                    <div className="font-semibold text-xs">{p.key}</div>
                    <div className="text-[11px] text-muted-foreground">{p.label}</div>
                  </td>
                  {roles.map(r => (
                    <td key={r} className="text-center px-3 py-2.5" data-testid={`perm-cell-${r}-${p.key}`}>
                      <Switch
                        checked={!!matrix[r]?.[p.key]}
                        onCheckedChange={() => toggle(r, p.key)}
                        disabled={isLocked(r)}
                        data-testid={`perm-switch-${r}-${p.key}`}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      <p className="text-[11px] text-muted-foreground">
        {editorRole === "developer"
          ? <><b>Developer</b> can edit every role, including <b>national_admin</b> and <b>developer</b>. Use with care.</>
          : <><b>national_admin</b> and <b>developer</b> rows are locked here — only a user with the <b>developer</b> role can modify them.</>
        }
      </p>
    </div>
  );
}
